import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { API_URL } from '../utils/apiConfig';
import GoogleLoginButton from '../components/GoogleLoginButton';

// Apple-inspired color palette
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  black: '#000000',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const SignUpPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [signupMethod, setSignupMethod] = useState('google'); // 'google' or 'email'
  const { user, signup, emailSignup, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if this is an invitation signup
  const returnUrl = searchParams.get('returnUrl');
  const prefilledEmail = searchParams.get('email');
  const plan = searchParams.get('plan'); // 'free' or 'paid'
  const interval = searchParams.get('interval') || 'yearly'; // 'yearly' or 'monthly'
  const isInvitationSignup = returnUrl && returnUrl.includes('join-workspace');
  const invitationToken = isInvitationSignup ? new URL(returnUrl, window.location.origin).searchParams.get('token') : null;

  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    jobTitle: '',
    useCase: '',
    teamMembers: [{ email: '', name: '', role: 'admin' }]
  });

  // Email signup form state
  const [emailFormData, setEmailFormData] = useState({
    name: '',
    email: prefilledEmail || '',
    password: '',
    confirmPassword: ''
  });

  // Redirect authenticated users to dashboard only if not in signup flow
  useEffect(() => {
    if (user && !plan && currentStep === 1) {
      // Only redirect if user is authenticated but not in a signup flow
      navigate('/dashboard');
    }
  }, [user, navigate, plan, currentStep]);

  // Simplified steps configuration - everyone gets free trial first
  const steps = [
    { id: 1, title: 'Account Setup', description: 'Connect your Google account' },
    { id: 2, title: 'Company Info', description: 'Tell us about your company' },
    { id: 3, title: 'Complete Setup', description: 'Start your free trial' }
  ];

  const useCaseOptions = [
    { value: 'Legal', label: 'Legal & Compliance' },
    { value: 'HR', label: 'Human Resources' },
    { value: 'Sales', label: 'Sales & Business Development' },
    { value: 'Personal', label: 'Personal Use' },
    { value: 'Other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailFormChange = (field, value) => {
    setEmailFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailSignup = async () => {
    try {
      setIsLoading(true);
      
      // Validation
      if (!emailFormData.name.trim()) {
        toast.error('Please enter your full name');
        return;
      }
      
      if (!emailFormData.email.trim()) {
        toast.error('Please enter your email address');
        return;
      }
      
      if (!emailFormData.password) {
        toast.error('Please enter a password');
        return;
      }
      
      if (emailFormData.password.length < 6) {
        toast.error('Password must be at least 6 characters long');
        return;
      }
      
      if (emailFormData.password !== emailFormData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      
      // If this is an invitation signup, use special invitation flow
      if (isInvitationSignup && invitationToken) {
        // For invitations, we'll handle this differently
        toast.error('Invitation signup with email is not yet supported. Please use Google signup for invitations.');
        return;
      }
      
      const user = await emailSignup({
        name: emailFormData.name.trim(),
        email: emailFormData.email.trim(),
        password: emailFormData.password
      });
      
      // Pre-fill form with user data
      setFormData(prev => ({
        ...prev,
        teamMembers: [{ email: user.email, name: user.name, role: 'admin' }]
      }));
      
      // Show verification message if needed
      if (user.needsEmailVerification) {
        toast.success('Account created! Please check your email to verify your account before proceeding.');
      } else {
        toast.success('Account created successfully!');
      }
      
      // Always go to company info step
      setCurrentStep(2);
    } catch (error) {
      console.error('Email signup failed:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error('An account with this email already exists. Please try logging in instead.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);
      
      // If this is an invitation signup, use special invitation flow
      if (isInvitationSignup && invitationToken) {
        await handleInvitationSignup(credentialResponse);
        return;
      }
      
      const user = await signup(credentialResponse);
      
      // Pre-fill form with user data
      setFormData(prev => ({
        ...prev,
        teamMembers: [{ email: user.email, name: user.name, role: 'admin' }]
      }));
      
      // Always go to company info step
      setCurrentStep(2);
    } catch (error) {
      console.error('Google signup failed:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error('An account with this email already exists. Please use the login flow instead.');
      } else {
        toast.error('Failed to sign up with Google. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvitationSignup = async (credentialResponse) => {
    try {
      console.log('🔄 Processing invitation signup with Google...');
      
      // Use the special invited user signup endpoint
      const response = await fetch(`${API_URL}/auth/invited-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: invitationToken,
          googleCredential: credentialResponse.credential
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sign up with invitation');
      }

      const data = await response.json();
      
      // Store the user data and token
      const completeUserData = {
        ...data.user,
        token: data.token
      };
      
      // Update auth context and localStorage
      localStorage.setItem('user', JSON.stringify(completeUserData));
      localStorage.setItem('token', data.token);
      
      // Force page reload to update auth context
      toast.success(data.message || 'Successfully joined workspace!');
      
      // Use window.location to ensure complete refresh and auth context update
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
      
    } catch (error) {
      console.error('❌ Invitation signup failed:', error);
      throw error; // Re-throw to be handled by the caller
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCompanyInfoNext = () => {
    if (!formData.companyName.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    
    if (!formData.jobTitle.trim()) {
      toast.error('Please enter your job title');
      return;
    }
    
    if (!formData.useCase.trim()) {
      toast.error('Please select your use case');
      return;
    }

    // Persist profile/company fields before moving forward
    (async () => {
      try {
        await updateProfile({
          companyName: formData.companyName.trim(),
          jobTitle: formData.jobTitle.trim(),
          useCase: formData.useCase.trim()
        });
        // Write a short-lived seed for onboarding prefill (merged then removed)
        localStorage.setItem('signupProfileSeed', JSON.stringify({
          companyName: formData.companyName.trim(),
          jobTitle: formData.jobTitle.trim(),
          useCase: formData.useCase.trim()
        }));
      } catch (e) {
        // Silent fail to avoid blocking UX; backend data refresh will still populate later
        console.warn('Profile pre-save during signup failed (non-blocking):', e?.message || e);
      } finally {
        // Always go to completion step after company info
        setCurrentStep(3);
      }
    })();
  };



  const handlePayment = async () => {
    try {
      setIsLoading(true);
      
      // Everyone starts with free trial - payment can be set up later in dashboard
      toast.success('Free trial started! You have 2 free documents to get started.');
      // Use window.location to ensure complete refresh and auth context update
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to start trial:', error);
      toast.error('Failed to start trial. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };





  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="text-center"
    >
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2" style={{ color: colors.gray900, ...fontStyles.heading }}>
          {isInvitationSignup ? 'Your team is waiting!' : 'Create Your Account'}
        </h2>
        <p className="text-sm text-gray-600 mb-1" style={{ ...fontStyles.body }}>
          {isInvitationSignup 
            ? 'Join in seconds with one click' 
            : 'Pick the fastest way to start signing'
          }
        </p>
        {!isInvitationSignup && (
          <p className="text-xs font-medium" style={{ color: colors.accent, ...fontStyles.medium }}>
            Get your documents signed in seconds - no credit card required
          </p>
        )}
      </div>

      {/* Premium Signup Method Toggle */}
      {!isInvitationSignup && (
        <div 
          className="relative p-1 border rounded-2xl transition-all duration-300 mb-6"
          style={{
            background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.8))',
            borderColor: 'rgba(30, 64, 175, 0.08)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            backdropFilter: 'blur(8px)'
          }}
        >
          {/* Sliding Background Indicator */}
          <div
            className="absolute top-1 bottom-1 rounded-xl transition-all duration-300 ease-out"
            style={{
              left: signupMethod === 'google' ? '4px' : '50%',
              width: signupMethod === 'google' ? 'calc(50% - 6px)' : 'calc(50% - 6px)',
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
              boxShadow: `0 2px 8px rgba(30, 64, 175, 0.2)`,
              zIndex: 1
            }}
          />
          
          <div className="relative flex z-10">
            <button
              onClick={() => setSignupMethod('google')}
              className="flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center relative rounded-xl"
              style={{
                color: signupMethod === 'google' ? colors.white : colors.gray700,
                ...fontStyles.medium,
                letterSpacing: '-0.01em',
                textShadow: signupMethod === 'google' ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
              }}
            >
              <span>Google</span>
            </button>
            
            <button
              onClick={() => setSignupMethod('email')}
              className="flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center relative rounded-xl"
              style={{
                color: signupMethod === 'email' ? colors.white : colors.gray700,
                ...fontStyles.medium,
                letterSpacing: '-0.01em',
                textShadow: signupMethod === 'email' ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
              }}
            >
              <span>Email</span>
            </button>
          </div>
        </div>
      )}

      {/* Google Signup */}
      {signupMethod === 'google' && (
        <GoogleLoginButton 
          onSuccess={handleGoogleSuccess}
          disabled={isLoading}
          text={isInvitationSignup ? "Join Workspace with Google" : "Continue with Google"}
          className="w-full"
        />
      )}

     

      {/* Enhanced Email Signup */}
      {signupMethod === 'email' && (
        <div className="space-y-5">
          <div>
            <input
              type="text"
              value={emailFormData.name}
              onChange={(e) => handleEmailFormChange('name', e.target.value)}
              placeholder="Full Name"
              className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(30, 64, 175, 0.12)',
                borderRadius: '16px',
                color: colors.gray900,
                ...fontStyles.body,
                letterSpacing: '-0.01em',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(4px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.accent;
                e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
              }}
            />
          </div>
          
          <div>
            <input
              type="email"
              value={emailFormData.email}
              onChange={(e) => handleEmailFormChange('email', e.target.value)}
              placeholder="Email Address"
              className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(30, 64, 175, 0.12)',
                borderRadius: '16px',
                color: colors.gray900,
                ...fontStyles.body,
                letterSpacing: '-0.01em',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(4px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.accent;
                e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
              }}
            />
          </div>
          
          <div>
            <input
              type="password"
              value={emailFormData.password}
              onChange={(e) => handleEmailFormChange('password', e.target.value)}
              placeholder="Password (6+ characters)"
              className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(30, 64, 175, 0.12)',
                borderRadius: '16px',
                color: colors.gray900,
                ...fontStyles.body,
                letterSpacing: '-0.01em',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(4px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.accent;
                e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
              }}
            />
          </div>
          
          <div>
            <input
              type="password"
              value={emailFormData.confirmPassword}
              onChange={(e) => handleEmailFormChange('confirmPassword', e.target.value)}
              placeholder="Confirm Password"
              className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(30, 64, 175, 0.12)',
                borderRadius: '16px',
                color: colors.gray900,
                ...fontStyles.body,
                letterSpacing: '-0.01em',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(4px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.accent;
                e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
              }}
            />
          </div>
          
          <button
            onClick={handleEmailSignup}
            disabled={isLoading}
            className="w-full px-6 py-4 font-medium transition-all duration-300 ease-out relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              borderRadius: '18px',
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
              color: colors.white,
              boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              ...fontStyles.medium,
              letterSpacing: '-0.01em',
              fontSize: '16px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              border: `1px solid rgba(255, 255, 255, 0.1)`
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                e.target.style.filter = 'brightness(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                e.target.style.filter = 'brightness(1)';
              }
            }}
          >
            {/* Shimmer effect */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                animation: 'shimmer 2s infinite'
              }}
            />
            
            <div className="relative z-10 flex items-center justify-center">
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight size={16} className="ml-3 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Enhanced Login Link */}
      <div 
        className="mt-8 pt-6 border-t"
        style={{
          borderColor: 'rgba(30, 64, 175, 0.08)'
        }}
      >
        <p 
          className="text-sm text-center"
          style={{
            color: colors.gray600,
            ...fontStyles.body
          }}
        >
          Already have an account?{' '}
          <Link 
            to="/login" 
            className="font-medium transition-all duration-300 relative inline-flex items-center"
            style={{
              color: colors.accent,
              ...fontStyles.medium,
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = colors.accentHover;
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = colors.accent;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <span>Sign in here</span>
            <ArrowRight 
              size={14} 
              className="ml-1 transition-transform duration-300" 
              style={{
                transform: 'translateX(0)',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateX(2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateX(0)';
              }}
            />
          </Link>
        </p>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="text-center mb-8">
        <h2 
          className="text-xl font-semibold mb-3"
          style={{ 
            color: colors.gray900, 
            ...fontStyles.heading,
            letterSpacing: '-0.02em'
          }}
        >
          Almost there!
        </h2>
        <p 
          className="text-sm leading-relaxed mb-2"
          style={{ 
            color: colors.gray700,
            ...fontStyles.body 
          }}
        >
          Just 30 seconds to unlock instant e-signatures
        </p>
        <p 
          className="text-xs font-medium"
          style={{ 
            color: colors.accent,
            ...fontStyles.medium 
          }}
        >
          No setup fees. No hidden costs. Start signing in minutes.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label 
            className="block text-sm font-medium mb-3"
            style={{
              color: colors.gray700,
              ...fontStyles.medium
            }}
          >
            Company Name *
          </label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Enter your company name"
            className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(30, 64, 175, 0.12)',
              borderRadius: '16px',
              color: colors.gray900,
              ...fontStyles.body,
              letterSpacing: '-0.01em',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(4px)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.accent;
              e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
            }}
          />
        </div>

        <div>
          <label 
            className="block text-sm font-medium mb-3"
            style={{
              color: colors.gray700,
              ...fontStyles.medium
            }}
          >
            Job Title *
          </label>
          <input
            type="text"
            value={formData.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e.target.value)}
            placeholder="Enter your job title"
            className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(30, 64, 175, 0.12)',
              borderRadius: '16px',
              color: colors.gray900,
              ...fontStyles.body,
              letterSpacing: '-0.01em',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(4px)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.accent;
              e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
            }}
          />
        </div>

        <div>
          <label 
            className="block text-sm font-medium mb-3"
            style={{
              color: colors.gray700,
              ...fontStyles.medium
            }}
          >
            What will you sign first? *
          </label>
          <select
            value={formData.useCase}
            onChange={(e) => handleInputChange('useCase', e.target.value)}
            className="w-full px-5 py-4 border transition-all duration-300 focus:outline-none appearance-none cursor-pointer"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(30, 64, 175, 0.12)',
              borderRadius: '16px',
              color: colors.gray900,
              ...fontStyles.body,
              letterSpacing: '-0.01em',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(4px)',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23636363'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              backgroundSize: '20px',
              paddingRight: '48px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.accent;
              e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(30, 64, 175, 0.12)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
            }}
          >
            <option value="">Select your use case</option>
            {useCaseOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex space-x-4 pt-6">
          <button
            onClick={handleBack}
            className="flex-1 px-6 py-4 font-medium transition-all duration-300 ease-out relative overflow-hidden group border"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
              borderColor: 'rgba(30, 64, 175, 0.15)',
              color: colors.gray700,
              borderRadius: '18px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              ...fontStyles.medium
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 100%)';
              e.target.style.borderColor = 'rgba(30, 64, 175, 0.2)';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
              e.target.style.borderColor = 'rgba(30, 64, 175, 0.15)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
            }}
          >
            Back
          </button>
          <button
            onClick={handleCompanyInfoNext}
            className="flex-1 px-6 py-4 font-medium transition-all duration-300 ease-out relative overflow-hidden group"
            style={{
              borderRadius: '18px',
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
              color: colors.white,
              boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              ...fontStyles.medium,
              letterSpacing: '-0.01em',
              fontSize: '16px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              border: `1px solid rgba(255, 255, 255, 0.1)`
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
              e.target.style.filter = 'brightness(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
              e.target.style.filter = 'brightness(1)';
            }}
          >
            {/* Shimmer effect */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                animation: 'shimmer 2s infinite'
              }}
            />
            <span className="relative z-10 flex items-center justify-center">
              Next
              <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </button>
        </div>
      </div>
    </motion.div>
  );



  const renderFreeTrialCompletion = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="text-center"
    >
      <div className="mb-8">
        <h2 
          className="text-xl font-semibold mb-3"
          style={{ 
            color: colors.gray900, 
            ...fontStyles.heading,
            letterSpacing: '-0.02em'
          }}
        >
          Welcome aboard! 🎉
        </h2>
        <p 
          className="text-sm leading-relaxed mb-2"
          style={{ 
            color: colors.gray700,
            ...fontStyles.body 
          }}
        >
          Your team can now sign documents 10x faster
        </p>
        <p 
          className="text-xs font-medium"
          style={{ 
            color: colors.accent,
            ...fontStyles.medium 
          }}
        >
          Your 2 free documents are ready - most teams sign their first document within 5 minutes!
        </p>
      </div>

      <div 
        className="border rounded-3xl p-6 mb-8 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.03) 0%, rgba(255, 255, 255, 0.95) 25%, rgba(248, 250, 252, 0.9) 100%)',
          borderColor: 'rgba(52, 199, 89, 0.12)',
          boxShadow: '0 8px 25px rgba(52, 199, 89, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)',
          backdropFilter: 'blur(8px)'
        }}
      >
        {/* Subtle gradient overlay */}
        <div 
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.02) 0%, transparent 50%)',
            borderRadius: '24px'
          }}
        />
        
        <div className="relative z-10">
          <div className="flex items-center justify-center mb-4">
            <div 
              className="p-3 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.08), rgba(52, 199, 89, 0.12))',
                boxShadow: '0 8px 20px rgba(52, 199, 89, 0.15)'
              }}
            >
              <Check 
                size={20} 
                style={{color: colors.success}}
              />
            </div>
          </div>
          
          <h3 
            className="text-sm font-semibold mb-4"
            style={{
              color: colors.success,
              ...fontStyles.medium
            }}
          >
            Free Trial Includes:
          </h3>
          
          <div className="space-y-3">
            <div 
              className="flex items-center text-sm justify-center"
              style={{
                color: colors.gray700,
                ...fontStyles.body
              }}
            >
              <Check size={16} className="mr-3" style={{color: colors.success}} />
              2 document signatures
            </div>
            <div 
              className="flex items-center text-sm justify-center"
              style={{
                color: colors.gray700,
                ...fontStyles.body
              }}
            >
              <Check size={16} className="mr-3" style={{color: colors.success}} />
              AI-powered contract creation
            </div>
            <div 
              className="flex items-center text-sm justify-center"
              style={{
                color: colors.gray700,
                ...fontStyles.body
              }}
            >
              <Check size={16} className="mr-3" style={{color: colors.success}} />
              Document management
            </div>
            <div 
              className="flex items-center text-sm justify-center"
              style={{
                color: colors.gray700,
                ...fontStyles.body
              }}
            >
              <Check size={16} className="mr-3" style={{color: colors.success}} />
              Email support
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={handleBack}
          className="flex-1 px-6 py-4 font-medium transition-all duration-300 ease-out relative overflow-hidden group border"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            borderColor: 'rgba(30, 64, 175, 0.15)',
            color: colors.gray700,
            borderRadius: '18px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            ...fontStyles.medium
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 100%)';
            e.target.style.borderColor = 'rgba(30, 64, 175, 0.2)';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
            e.target.style.borderColor = 'rgba(30, 64, 175, 0.15)';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
          }}
        >
          Back
        </button>
        <button
          onClick={handlePayment}
          disabled={isLoading}
          className="flex-1 px-6 py-4 font-medium transition-all duration-300 ease-out relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            borderRadius: '18px',
            background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
            color: colors.white,
            boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            ...fontStyles.medium,
            letterSpacing: '-0.01em',
            fontSize: '16px',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            border: `1px solid rgba(255, 255, 255, 0.1)`
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
              e.target.style.filter = 'brightness(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
              e.target.style.filter = 'brightness(1)';
            }
          }}
        >
          {/* Shimmer effect */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
              animation: 'shimmer 2s infinite'
            }}
          />
          
          <div className="relative z-10 flex items-center justify-center">
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Starting Trial...
              </>
            ) : (
              <>
                Start Signing Now
                <ArrowRight size={16} className="ml-3 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </div>
        </button>
      </div>
    </motion.div>
  );



  return (
    <div
      className="min-h-screen bg-white py-12 px-6"
      style={{
        fontFamily: fontStyles.fontFamily,
        backgroundColor: colors.white,
        minHeight: '100vh',
        padding: '3rem 1.5rem',
      }}
    >
      <div
        className="w-full max-w-lg mx-auto"
        style={{
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Header Section */}
        <div className="text-center mb-10">
          {/* Main Heading with inline logo */}
          <h1
            className="text-4xl font-bold mb-4 flex items-center justify-center"
            style={{
              color: colors.black,
              ...fontStyles.heading,
              lineHeight: '1.1',
            }}
          >
            Get started with
            <img src="/6.png" alt="SignPact Logo" className="h-12 ml-2" style={{marginTop: '4px'}} />
          </h1>
          
          
          {isInvitationSignup && prefilledEmail && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Workspace Invitation:</strong> You're signing up to join a team workspace for {prefilledEmail}
              </p>
            </div>
          )}
        </div>

        {/* Enhanced Step Indicator - Premium Design */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div 
                className="flex items-center justify-center w-10 h-10 rounded-full border-2 text-xs transition-all duration-300 ease-out"
                style={{
                  backgroundColor: currentStep >= step.id ? colors.accent : 'transparent',
                  borderColor: currentStep >= step.id ? colors.accent : colors.gray300,
                  color: currentStep >= step.id ? colors.white : colors.gray400,
                  boxShadow: currentStep >= step.id 
                    ? `0 4px 12px ${colors.accent}30, inset 0 1px 0 rgba(255, 255, 255, 0.2)` 
                    : 'none',
                  ...fontStyles.medium
                }}
              >
                {currentStep > step.id ? (
                  <Check size={16} />
                ) : (
                  <span className="text-xs font-semibold">{step.id}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div 
                  className="w-12 h-0.5 mx-2 transition-all duration-300 ease-out"
                  style={{
                    backgroundColor: currentStep > step.id ? colors.accent : colors.gray300,
                    boxShadow: currentStep > step.id ? `0 2px 4px ${colors.accent}20` : 'none'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Enhanced Step Content - Premium Container */}
        <div 
          className="border rounded-3xl p-8 mb-8 transition-all duration-300 ease-out relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
            borderColor: 'rgba(30, 64, 175, 0.08)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.06), 0 8px 25px rgba(0, 0, 0, 0.04)',
            backdropFilter: 'blur(12px)',
            borderRadius: '32px'
          }}
        >
          {/* Subtle gradient overlay */}
          <div 
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.01) 0%, transparent 50%)',
              borderRadius: '32px'
            }}
          />
          
          <div className="relative z-10">
            <AnimatePresence mode="wait">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderFreeTrialCompletion()}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer Section */}
        <div className="text-center">
          {/* Enhanced Demo booking call-to-action - Matching design system */}
          <div 
            className="mb-8 p-6 border rounded-3xl transition-all duration-300 ease-out relative overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(255, 255, 255, 0.95) 25%, rgba(249, 249, 249, 0.9) 100%)',
              borderColor: 'rgba(30, 64, 175, 0.12)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(8px)',
              borderRadius: '24px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.08), 0 6px 15px rgba(0, 0, 0, 0.06)';
              e.currentTarget.style.borderColor = 'rgba(30, 64, 175, 0.18)';
              e.currentTarget.style.filter = 'brightness(1.01)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04)';
              e.currentTarget.style.borderColor = 'rgba(30, 64, 175, 0.12)';
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {/* Subtle gradient overlay */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.02) 0%, transparent 50%)',
                borderRadius: '24px'
              }}
            />
            
            <div className="relative z-10">
              <div className="flex items-center justify-center mb-4">
                <div 
                  className="p-3 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.08), rgba(30, 64, 175, 0.12))',
                    boxShadow: '0 8px 20px rgba(30, 64, 175, 0.15)'
                  }}
                >
                  <ArrowRight 
                    size={20} 
                    style={{color: colors.accent}}
                  />
                </div>
              </div>
              
              <h3 
                className="text-lg font-semibold mb-3"
                style={{
                  color: colors.gray900,
                  ...fontStyles.heading,
                  letterSpacing: '-0.02em'
                }}
              >
                Need More Than e-Signatures?
              </h3>
              
              <p 
                className="text-sm mb-2 max-w-md mx-auto leading-relaxed"
                style={{
                  color: colors.gray700,
                  ...fontStyles.body,
                  lineHeight: '1.5'
                }}
              >
                Have more complex needs than just the e-signature workflow? Want to automate your entire contract workflow?
              </p>
              
              <a
                href="https://cal.com/ranvirdeshmukh/book-a-demo-signpact"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 font-medium transition-all duration-300 ease-out relative overflow-hidden group"
                style={{
                  borderRadius: '16px',
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                  color: colors.white,
                  boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '15px',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                }}
                onMouseDown={(e) => {
                  e.target.style.transform = 'translateY(0) scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                }}
              >
                {/* Shimmer effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                    animation: 'shimmer 2s infinite'
                  }}
                />
                <span className="relative z-10 mr-3">Book a Demo</span>
                <ArrowRight size={16} className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>

          <p
            className="text-xs leading-relaxed mb-4"
            style={{
              color: colors.gray500,
              fontSize: '12px',
            }}
          >
            By clicking continue, you agree to our{' '}
            <Link
              to="/terms"
              className="underline hover:no-underline"
              style={{ color: colors.accent }}
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              className="underline hover:no-underline"
              style={{ color: colors.accent }}
            >
              Privacy Policy
            </Link>
            .
          </p>
          
          {/* Reassurance & Support */}
          <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: colors.gray600 }}>
                Cancel anytime • SSL encrypted • SOC 2 planned (Q1 2026) • Average setup: 2 minutes
              </p>
            <p className="text-xs text-gray-500">
              Need help? Contact our support team at{' '}
              <a href="mailto:support@signpact.com" className="text-blue-600 hover:text-blue-800">
                support@signpact.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage; 